/* build 20260825-r2: hero drag-to-fold frame sequence; no-ops until a frame pattern + count are set */
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  function initStage(stage) {
    if (stage.hasAttribute('data-tx-ready')) return;
    stage.setAttribute('data-tx-ready', '');
    var pattern = stage.getAttribute('data-scrub-pattern');
    var count = parseInt(stage.getAttribute('data-scrub-count') || '0', 10);
    var img = stage.querySelector('[data-scrub-poster]');
    if (!pattern || !count || count < 2 || !img) return; // poster-only mode

    var isMobileStage = stage.className.indexOf('tx-hero__stage--m') !== -1;

    /* data-scrub-start is 1-based; the poster must be that frame's art or the first drag jumps */
    var start = parseInt(stage.getAttribute('data-scrub-start') || '1', 10);
    var restIx = Math.max(0, Math.min(count - 1, start - 1));
    var frames = [];
    var loaded = 0;
    var current = restIx;
    var pxPerFrame = Math.max(4, Math.round(stage.clientWidth / count));

    for (var i = 0; i < count; i++) {
      var f = new Image();
      f.src = pattern.replace('{n}', String(i + 1));
      f.onload = function () { loaded++; };
      frames.push(f);
    }

    /* TX 26/08/2026 (Daniel): pill stays put during the ambient loop; a self-sliding handle reads as broken */
    var pillLocked = true;
    function show(ix) {
      ix = Math.max(0, Math.min(count - 1, ix));
      if (ix === current) return;
      current = ix;
      if (frames[ix].complete && frames[ix].naturalWidth) img.src = frames[ix].src;
      if (!pillLocked) stage.style.setProperty('--tx-scrub-p', String(ix / (count - 1)));
    }

    /* Keep a reference to the observer: an unreferenced one can be garbage-collected and the gate latches */
    var inView = true;
    var heroIo = null;
    if ('IntersectionObserver' in window) {
      heroIo = new IntersectionObserver(function (entries) {
        inView = entries[0].isIntersecting;
      });
      heroIo.observe(stage);
      stage._txIo = heroIo;
    }

    function allReady() { return loaded >= count; }

    if (isMobileStage) {
      if (REDUCED && REDUCED.matches) return;
      var FPS = 10; // the source GIF's cadence
      var last = null;
      var acc = 0;
      var loopIx = 0;
      var started = false;
      function loop(now) {
        if (last === null) last = now;
        var dt = now - last;
        last = now;
        if (inView && !document.hidden && allReady()) {
          if (!started) { started = true; loopIx = 0; }
          acc += dt;
          if (acc >= 1000 / FPS) {
            acc = 0;
            loopIx = (loopIx + 1) % count;
            show(loopIx);
          }
        }
        if (stage.isConnected) requestAnimationFrame(loop);
        else if (heroIo) heroIo.disconnect();
      }
      requestAnimationFrame(loop);
      return;
    }

    /* TX 26/08/2026 (Daniel): desktop autoplay loops (unwind, fold, hold) until the first pointerdown */
    var autoplayDone = REDUCED && REDUCED.matches;
    if (autoplayDone) pillLocked = false;
    var phase = 'unwind';
    var holdUntil = 0;
    function autoplay(now) {
      if (autoplayDone) return;
      if (!stage.isConnected) { if (heroIo) heroIo.disconnect(); return; }
      if (!inView || document.hidden || !allReady()) { requestAnimationFrame(autoplay); return; }
      if (now < holdUntil) { requestAnimationFrame(autoplay); return; }
      if (phase === 'unwind') {
        show(current - 2);
        if (current <= 0) { phase = 'fold'; holdUntil = now + 250; }
        else holdUntil = now + 1000 / 30;
      } else {
        show(current + 1);
        if (current >= count - 1) { phase = 'unwind'; holdUntil = now + 1200; }
        else holdUntil = now + 1000 / 14;
      }
      requestAnimationFrame(autoplay);
    }
    requestAnimationFrame(autoplay);

    var dragging = false;
    var startX = 0;
    var startFrame = 0;

    stage.addEventListener('pointerdown', function (e) {
      autoplayDone = true;
      if (pillLocked) {
        pillLocked = false;
        stage.style.setProperty('--tx-scrub-p', String(current / (count - 1)));
      }
      dragging = true;
      startX = e.clientX;
      startFrame = current;
      stage.setPointerCapture(e.pointerId);
      stage.classList.add('is-scrubbing');
    });
    stage.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      show(startFrame + Math.round((e.clientX - startX) / pxPerFrame));
    });
    ['pointerup', 'pointercancel'].forEach(function (ev) {
      stage.addEventListener(ev, function () {
        dragging = false;
        stage.classList.remove('is-scrubbing');
      });
    });
  }

  /* TX 14/09/2026: reduced motion rests stage videos on the first frame */
  function initVideos() {
    if (!REDUCED || !REDUCED.matches) return;
    document.querySelectorAll('[data-tx-hero-video]').forEach(function (v) {
      v.removeAttribute('autoplay');
      v.pause();
    });
  }

  /* TX 14/10/2026 (Tack): background / split films; a visitor's pause (data-paused) is never overridden */
  function initLoop(v) {
    if (v.hasAttribute('data-tx-ready')) return;
    v.setAttribute('data-tx-ready', '');
    var q = v.getAttribute('data-tx-hero-mq');
    var m = q && window.matchMedia ? window.matchMedia(q) : null;
    var seen = true;
    function on() {
      if (!v.isConnected || (m && !m.matches)) return;
      if (!v.getAttribute('poster') && v.getAttribute('data-poster')) v.setAttribute('poster', v.getAttribute('data-poster'));
      if (!v.getAttribute('src')) v.src = v.getAttribute('data-src');
      if ((REDUCED && REDUCED.matches) || v.getAttribute('data-paused') === '1' || !seen) return;
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    }
    on();
    if (m && m.addEventListener) {
      var onChange = function () {
        if (m.matches) on();
        else if (v.getAttribute('src')) v.pause();
      };
      m.addEventListener('change', onChange);
      v._txOff = function () { m.removeEventListener('change', onChange); };
    }
    if ('IntersectionObserver' in window) {
      v._txIo = new IntersectionObserver(function (entries) {
        seen = entries[0].isIntersecting;
        if (seen) on();
        else if (!v.paused) v.pause();
      });
      v._txIo.observe(v);
    }
  }

  /* TX 14/10/2026 (Tack): pause button, WCAG 2.2.2 */
  function initPause(b) {
    if (b.hasAttribute('data-tx-ready')) return;
    b.setAttribute('data-tx-ready', '');
    var v = document.getElementById(b.getAttribute('data-tx-hero-vpause'));
    if (!v) { b.hidden = true; return; }
    function sync() {
      var paused = v.paused;
      b.setAttribute('aria-pressed', paused ? 'true' : 'false');
      b.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
    }
    v.addEventListener('play', sync);
    v.addEventListener('pause', sync);
    b.addEventListener('click', function () {
      if (v.paused) {
        v.removeAttribute('data-paused');
        if (!v.getAttribute('src')) v.src = v.getAttribute('data-src');
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
      } else {
        v.setAttribute('data-paused', '1');
        v.pause();
      }
    });
    sync();
  }

  function bootIn(root) {
    root.querySelectorAll('[data-tx-hero-stage]').forEach(initStage);
    root.querySelectorAll('[data-tx-hero-mq]').forEach(initLoop);
    root.querySelectorAll('[data-tx-hero-vpause]').forEach(initPause);
  }

  function boot() {
    bootIn(document);
    initVideos();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  /* TX 14/10/2026 (Tack): editor re-render, wire up the new nodes only */
  document.addEventListener('shopify:section:load', function (e) {
    bootIn(e.target);
    initVideos();
  });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('video[data-tx-hero-video]').forEach(function (v) {
      v.pause();
      if (v._txIo) v._txIo.disconnect();
      if (v._txOff) v._txOff();
    });
    e.target.querySelectorAll('[data-tx-hero-stage]').forEach(function (s) {
      if (s._txIo) s._txIo.disconnect();
    });
  });
})();
